import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import ClearIcon from "@mui/icons-material/Clear";
import FlagIcon from "@mui/icons-material/Flag";
import { Autocomplete, Avatar, Box, Checkbox, debounce } from "@mui/material";
import { PropTypes } from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { Loader, OverflowTags, TagItem } from "@/atoms";
import { constantsData, identifiersData } from "@/data";
import { useGetLastElement } from "@/hooks";
import { setSelection } from "@/redux/actions";
import { StyledTextField } from "@/styles";
import { calculateTagsVisibilityHandler } from "@/utils";

const AutoCompleteMenu = ({
    asyncMenuData,
    defaultValue,
    disabled,
    disabledOption,
    disabledOptions,
    errors,
    filterValue,
    filtersKeys,
    getRenderedOption,
    hasFilter,
    hasSmallField,
    isAsyncMenu,
    isCapped,
    isCountriesMenu,
    isFilterInput,
    isMultiple,
    isOptional,
    label,
    loading,
    maxSelectable,
    name,
    onChange,
    optionLabel,
    options,
    selectAllOption,
    selectedOptions,
    size,
    withoutLabel,
    withoutLabelTranslation,
}) => {
    const [selectedOption, setSelectedOption] = useState(isMultiple ? defaultValue || [] : defaultValue || null);

    const [textFieldValue, setTextFieldValue] = useState("");

    const [fetching, setFetching] = useState(false);

    const dispatch = useDispatch();

    const { t } = useTranslation();

    const [hoveredChip, setHoveredChip] = useState(null);
    const [overflowAnchor, setOverflowAnchor] = useState(null);
    const [visibleChipCount, setVisibleChipCount] = useState(1);
    const containerRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const calculateVisibleChips = () => {
            const container = containerRef.current;
            const containerWidth = container?.offsetWidth || 0;
            let count = 1;

            if (containerWidth >= 500) {
                const chipWidth = 94;
                const reservedSpace = 40;
                const availableWidth = Math.max(0, containerWidth * 0.75 - reservedSpace);
                count = Math.max(1, Math.floor(availableWidth / chipWidth));
            }
            setVisibleChipCount(count);
        };

        calculateVisibleChips();

        const resizeObserver = new ResizeObserver(() => {
            calculateVisibleChips();
        });

        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [selectedOption]);

    useEffect(() => {
        if (hoveredChip !== null) {
            const isStillPresent = isMultiple
                ? selectedOption?.some((item) => item.value === hoveredChip)
                : selectedOption?.value === hoveredChip;

            if (!isStillPresent) {
                setHoveredChip(null);
                setOverflowAnchor(null);
            }
        }
    }, [selectedOption, hoveredChip, isMultiple]);

    const {
        count: asyncMenuCount,
        loading: asyncMenuLoading,
        nextCursor: asyncMenuNextCursor,
        page: asyncMenuPage,
        refetch: asyncMenuRefetch,
        searchKey: asyncMenuSearchKey,
        selectionKey: asyncMenuSelectionKey,
    } = asyncMenuData || {};

    const { lastElement } = useGetLastElement(asyncMenuLoading, options?.length < asyncMenuCount, () => {
        asyncMenuRefetch({
            cursor: asyncMenuNextCursor ? { id: asyncMenuNextCursor } : undefined,
            limit: 25,
            page: !asyncMenuNextCursor && asyncMenuPage + 1,
            where: {
                ...(textFieldValue && {
                    [asyncMenuSearchKey]: { contains: textFieldValue },
                }),
                ...(hasFilter && {
                    AND: [
                        {
                            [filtersKeys[0]]: {
                                some: {
                                    [filtersKeys[1]]: { equals: filterValue },
                                },
                            },
                        },
                    ],
                }),
            },
        });
    });

    // a select all menu is controlled: its select all option is ticked only while every option is selected and it
    // never reaches onChange or the chips, which always hold the real options
    const isSelectAllMenu = !!isMultiple && !!selectAllOption;

    const hasSelectAllOption = isSelectAllMenu && options?.length > 0;

    const isAllSelected =
        hasSelectAllOption &&
        options.every((option) => selectedOption?.some((selected) => selected?.value === option?.value));

    // the select all option goes last so the chips indexes still line up with getItemProps once it is dropped
    const selectAllMenuValue = isAllSelected ? [...selectedOption, selectAllOption] : selectedOption || [];

    let inputLabel = "";

    if (withoutLabelTranslation) inputLabel = label;
    else inputLabel = t(`labels.${label}`);

    const fetchSearchDataHandler = (value) => {
        if (!asyncMenuRefetch || typeof asyncMenuRefetch !== "function") return;

        if (value !== undefined) {
            if (value) {
                dispatch(setSelection({ [asyncMenuSelectionKey]: [] }));
                asyncMenuRefetch({
                    cursor: undefined,
                    limit: 25,
                    page: !asyncMenuNextCursor && 0,
                    where: {
                        [asyncMenuSearchKey]: { contains: value },
                        ...(hasFilter && {
                            AND: [
                                {
                                    [filtersKeys[0]]: {
                                        some: {
                                            [filtersKeys[1]]: { equals: filterValue },
                                        },
                                    },
                                },
                            ],
                        }),
                    },
                });
            }

            if (!value) {
                asyncMenuRefetch({
                    cursor: undefined,
                    limit: 25,
                    page: !asyncMenuNextCursor && 0,
                    where: {
                        ...(hasFilter && {
                            AND: [
                                {
                                    [filtersKeys[0]]: {
                                        some: {
                                            [filtersKeys[1]]: { equals: filterValue },
                                        },
                                    },
                                },
                            ],
                        }),
                    },
                });
            }
        }
    };

    const callSearchDataHandler = useCallback(
        debounce((value) => {
            fetchSearchDataHandler(value);
        }, 700),
        [],
    ); /* eslint react-hooks/exhaustive-deps: "off" */

    const changeTextFieldValueHandler = (e) => {
        setFetching(true);

        if (e) {
            const { value } = e.target;

            setTextFieldValue(value);

            callSearchDataHandler(value);
        }
    };

    // toggling the select all option selects or clears every option, any other change just drops it from the value
    const resolveSelectAllValueHandler = (option, reason, details) => {
        if (details?.option?.value === selectAllOption.value) return reason === "selectOption" ? options : [];

        return (option || []).filter(({ value }) => value !== selectAllOption.value);
    };

    const changeMenuValueHandler = (e, value, reason, details) => {
        const option = hasSelectAllOption ? resolveSelectAllValueHandler(value, reason, details) : value;

        onChange(e, option);

        const isClear = e?.target?.classList?.contains(identifiersData.classNames.clearIndicator);
        const isEmptyArray = Array.isArray(option) && option.length === 0;
        const newValue = isClear || isEmptyArray ? null : option;

        setSelectedOption(newValue);

        if (isAsyncMenu && (isClear || isEmptyArray)) fetchSearchDataHandler("");
    };

    useEffect(() => {
        if (!asyncMenuLoading && fetching) setFetching(false);
    }, [options, asyncMenuLoading]);

    return (
        <Autocomplete
            autoComplete={false}
            disableCloseOnSelect={isMultiple}
            disabled={loading || disabled}
            getOptionLabel={(option) => (typeof optionLabel === "function" ? optionLabel(option) : option?.label || "")}
            id={`${name}_${identifiersData.ids.input}`}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            multiple={!!isMultiple}
            options={hasSelectAllOption ? [selectAllOption, ...options] : options || []}
            ref={containerRef}
            size={size}
            clearIcon={
                <ClearIcon
                    fontSize="small"
                    onClick={() => {
                        if (isAsyncMenu) {
                            setSelectedOption(null);

                            fetchSearchDataHandler("");
                        }
                    }}
                />
            }
            {...(isSelectAllMenu
                ? { value: selectAllMenuValue }
                : {
                      defaultValue: isMultiple
                          ? selectedOption || []
                          : defaultValue || {
                                label: "",
                                value: "",
                            },
                  })}
            getOptionDisabled={(option) => {
                if (option.value === disabledOption) return true;

                if (
                    Array.isArray(disabledOptions) &&
                    disabledOptions.some((disabledValue) => disabledValue === option.value)
                )
                    return true;

                const atCapped =
                    !!isCapped &&
                    !!isMultiple &&
                    typeof maxSelectable === "number" &&
                    maxSelectable > 0 &&
                    Array.isArray(selectedOptions) &&
                    selectedOptions.length >= maxSelectable;

                if (!atCapped) return false;

                const alreadySelected = selectedOptions.some(({ value }) => value === option?.value);

                return !alreadySelected;
            }}
            noOptionsText={
                isAsyncMenu && (asyncMenuLoading || fetching) ? (
                    <Loader size={20} withoutFullHeight />
                ) : (
                    t("labels.noOptions")
                )
            }
            renderInput={(params) => (
                <StyledTextField
                    {...params}
                    autoComplete="off"
                    className={hasSmallField && identifiersData.classNames.smallField}
                    disabled={disabled}
                    error={errors?.[name]}
                    helperText={errors?.[name] && errors?.[name]?.message}
                    label={!withoutLabel && inputLabel + (isOptional ? ` (${t("labels.optional")})` : "")}
                    name={name}
                    placeholder={!withoutLabel && inputLabel + (isOptional ? ` (${t("labels.optional")})` : "")}
                    valuesLength={defaultValue?.length}
                    variant="outlined"
                    isFilterInput
                    onBlur={() => {
                        if ((!selectedOption || (selectedOption && isMultiple)) && textFieldValue && isAsyncMenu) {
                            setTextFieldValue("");

                            fetchSearchDataHandler("");
                        }
                    }}
                    onChange={(e) => {
                        if (isAsyncMenu) changeTextFieldValueHandler(e);
                    }}
                />
            )}
            renderOption={(props, option, { selected }) => (
                <Box key={option?.value || option?.id || option?.label}>
                    <Box
                        {...props}
                        {...(isAsyncMenu
                            ? {
                                  ref:
                                      options?.length === props?.[constantsData.dataOptionIndex] + 1
                                          ? lastElement
                                          : null,
                              }
                            : null)}
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            fontSize: 14,
                            gap: 1,
                            px: 1.5,
                            py: 0.5,
                        }}
                    >
                        {isCountriesMenu ? (
                            <>
                                <Checkbox
                                    checked={selected}
                                    checkedIcon={<CheckBoxIcon color="primary" />}
                                    icon={<CheckBoxOutlineBlankIcon />}
                                />
                                <Avatar
                                    alt={t("imageAlternatives.country")}
                                    slotProps={{ img: { loading: "lazy" } }}
                                    src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                                    sx={{
                                        height: 20,
                                        width: 20,
                                    }}
                                >
                                    <FlagIcon />
                                </Avatar>
                            </>
                        ) : (
                            <Checkbox
                                checked={selected}
                                checkedIcon={<CheckBoxIcon color="primary" />}
                                icon={<CheckBoxOutlineBlankIcon />}
                            />
                        )}
                        {getRenderedOption(option)}
                    </Box>
                    {asyncMenuLoading && props?.[constantsData.dataOptionIndex] + 1 === options.length && (
                        <Loader size={20} withoutFullHeight />
                    )}
                </Box>
            )}
            {...(isMultiple
                ? {
                      renderValue: (selectedValue, getItemProps) => {
                          const value = hasSelectAllOption
                              ? selectedValue.filter((option) => option?.value !== selectAllOption.value)
                              : selectedValue;

                          const { hiddenItems, visibleItems } = calculateTagsVisibilityHandler({
                              containerRef,
                              hoveredChip,
                              value,
                              visibleChipCount,
                          });

                          return (
                              <>
                                  {visibleItems.map((option) => (
                                      <TagItem
                                          getItemProps={getItemProps}
                                          hoverTimeoutRef={hoverTimeoutRef}
                                          index={value.indexOf(option)}
                                          isHovered={hoveredChip === option.value}
                                          key={option.value}
                                          option={option}
                                          setHoveredChip={setHoveredChip}
                                      />
                                  ))}

                                  {hiddenItems.length > 0 && (
                                      <OverflowTags
                                          getItemProps={getItemProps}
                                          hiddenItems={hiddenItems}
                                          hoverTimeoutRef={hoverTimeoutRef}
                                          isFilterInput={isFilterInput}
                                          overflowAnchor={overflowAnchor}
                                          setHoveredChip={setHoveredChip}
                                          setOverflowAnchor={setOverflowAnchor}
                                          value={value}
                                      />
                                  )}
                              </>
                          );
                      },
                  }
                : {})}
            sx={{
                "& .MuiAutocomplete-root": { zIndex: "99999 !important" },
                "& .MuiOutlinedInput-root": {
                    alignItems: "center",
                    display: "flex",
                    gap: "3px",
                    height: "auto",
                },
            }}
            openOnFocus
            onChange={(e, option, reason, details) => {
                changeMenuValueHandler(e, option, reason, details);
            }}
            onOpen={() => {
                setOverflowAnchor(null);
            }}
        />
    );
};

export default AutoCompleteMenu;

AutoCompleteMenu.propTypes = {
    asyncMenuData: PropTypes.object,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
    disabled: PropTypes.bool,
    disabledOption: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disabledOptions: PropTypes.array,
    errors: PropTypes.object,
    filterValue: PropTypes.string,
    filtersKeys: PropTypes.array,
    getRenderedOption: PropTypes.func,
    hasFilter: PropTypes.bool,
    hasSmallField: PropTypes.bool,
    isAsyncMenu: PropTypes.bool,
    isCapped: PropTypes.bool,
    isCountriesMenu: PropTypes.bool,
    isFilterInput: PropTypes.bool,
    isMultiple: PropTypes.bool,
    isOptional: PropTypes.bool,
    label: PropTypes.string,
    loading: PropTypes.bool,
    maxSelectable: PropTypes.number,
    name: PropTypes.string,
    onChange: PropTypes.func,
    optionLabel: PropTypes.func,
    options: PropTypes.array,
    selectAllOption: PropTypes.object,
    selectedOptions: PropTypes.array,
    size: PropTypes.string,
    withoutLabel: PropTypes.bool,
    withoutLabelTranslation: PropTypes.bool,
};
